import {observer} from "mobx-react-lite";
import {Button, TextField, Typography, Box, Paper, InputAdornment} from "@mui/material";
import React, {useCallback, useContext, useEffect} from "react";
import {toast} from "react-toastify";
import Logo from "../assets/logo.png";
import {AuthContext} from "../stores/AuthStore";
import {ApiContext} from "../api/api";
import {useMobx} from "../hooks/useMobx";
import {useLocation, useNavigate} from "react-router";
import {AppRoutes} from "../AppRoutes";
import {Frame} from "../components/common/Frame";
import {D} from "../Constants";
import {LoadingOverlay} from "../components/common/LoadingOverlay";
import {PersonOutline, LockOutlined} from "@mui/icons-material";

export const LoginPage = observer(() => {
  const auth = useContext(AuthContext)!;
  const api = useContext(ApiContext)!;

  const state = useMobx(() => ({
    username: undefined as string | undefined,
    password: undefined as string | undefined,
    loading: false,
  }));

  // redirect back to where the user was headed (or home) once authenticated
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (auth.authenticated) {
      const from = (location.state as { from?: string } | null)?.from;
      // only internal paths - never a full URL someone put into the state
      const target = from && from.startsWith('/') && !from.startsWith('//') ? from : AppRoutes.index;
      navigate(target, {replace: true});
    }
  }, [navigate, auth.authenticated, location.state]);

  // handle form submission
  /* eslint-disable react-hooks/exhaustive-deps */
  const handleSubmit = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();

    if (!state.username || !state.password) {
      state.username ??= '';
      state.password ??= '';
      toast.warning('Vnesite uporabniško ime in geslo');
      return;
    }

    state.loading = true;
    try {
      const response = await api.token.tokenObtainPair({
        username: state.username,
        password: state.password,
      }, {secure: false});

      if (response.ok && response.data.access) {
        auth.setAuth({
          access: response.data.access,
          refresh: response.data.refresh,
        });
      }
    } catch (e) {
      if (e instanceof Response) {
        if (e.status === 401) {
          toast.error('Napačno uporabniško ime ali geslo');
        } else {
          toast.error(`Napaka pri prijavi: ${e.status}`);
        }
      } else {
        toast.error(`Neznana napaka: ${e}`);
      }
    }
    state.loading = false;
  }, [state]);
  /* eslint-enable react-hooks/exhaustive-deps */

  return (
    <Frame center height={'100vh'} grow style={{background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'}}>
      <Paper
        elevation={8}
        sx={{
          maxWidth: '500px',
          width: '100%',
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: D.unit(5),
            position: 'relative'
          }}
        >
          <Box
            sx={{mb: D.unit(2)}}
          >
            <img src={Logo} alt="logo" width={D.unit(35)}/>
          </Box>

          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            textAlign={'center'}
          >
            Prijava v portal METKA
          </Typography>

          <Typography
            variant="subtitle1"
            color={'contentShade'}
            gutterBottom
            textAlign={'center'}
          >
            Vnesite svoje uporabniško ime in geslo.
          </Typography>

          <form onSubmit={handleSubmit} style={{ width: '100%' }} aria-label="Login form">
            <TextField
              label="Uporabniško ime"
              autoComplete="username"
              value={state.username}
              error={state.username === ''}
              onChange={(e) => state.username = e.target.value}
              fullWidth
              aria-required="true"
              margin="normal"
              variant="outlined"
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position="start">
                    <PersonOutline color={state.username === '' ? "error" : "primary"}/>
                  </InputAdornment>
                }
              }}
            />

            <TextField
              label="Geslo"
              value={state.password}
              error={state.password === ''}
              type="password"
              autoComplete="current-password"
              onChange={(e) => state.password = e.target.value}
              fullWidth
              margin="normal"
              variant="outlined"
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position="start">
                    <LockOutlined color={state.password === '' ? "error" : "primary"}/>
                  </InputAdornment>
                }
              }}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              sx={{
                mt: D.unit(2),
                mb: D.unit(2),
                py: D.unit(1.5),
              }}
            >
              PRIJAVA
            </Button>
          </form>

          <Typography variant="body2" color="textSecondary" sx={{mt: D.unit(2)}}>
            © {new Date().getFullYear()} LGM, UL FRI
          </Typography>

          <LoadingOverlay visible={state.loading}/>
        </Box>
      </Paper>
    </Frame>
  );
});
